type Timeout = ReturnType<typeof setTimeout>;

interface IMutatorTools {
  shuffleArray<T>(array: T[], makeCopy?: boolean): T[];
  roundToNearestTenth(num: number): number;
  formatText(
    message: string,
    values: Record<string, unknown>,
    useHTML?: boolean
  ): string;
  getTodaysDate(): string;
  isEmpty(obj: object | unknown[]): boolean;
  intersection<T>(arrays: T[][]): T[];
  getSample<T>(arr: T[], n: number): T[];
  capitalize(str: string): string;
  classList(elt: HTMLElement): {
    toggle: (c: string) => {
      toggle: (c: string) => any;
      add: (c: string) => any;
      remove: (c: string) => any;
    };
    add: (c: string) => {
      toggle: (c: string) => any;
      add: (c: string) => any;
      remove: (c: string) => any;
    };
    remove: (c: string) => {
      toggle: (c: string) => any;
      add: (c: string) => any;
      remove: (c: string) => any;
    };
  };
  createUniqueKey(): string;
  flattenObject(
    obj: Record<string, unknown>,
    parentKey?: string
  ): Record<string, unknown>;
  characters(typedText: string): void;
}

export default class MutatorTools implements IMutatorTools {
  private timeouts: Timeout[] = [];
  private typedChars: string = '';
  private delay: number = 50; // Assuming a default delay of 50ms
  private gamePlayStore: { setTypedChars: (chars: string) => void } | null =
    null;

  /**
   * Shuffles an array in place or creates a shuffled copy.
   * @param array - The array to shuffle.
   * @param makeCopy - Whether to create a copy of the array before shuffling.
   * @returns The shuffled array.
   */
  shuffleArray<T>(array: T[], makeCopy = false): T[] {
    const currentArray = makeCopy ? [...array] : array;
    let m = currentArray.length;

    while (m) {
      const i = Math.floor(Math.random() * m--);
      [currentArray[m], currentArray[i]] = [currentArray[i], currentArray[m]];
    }

    return currentArray;
  }

  /**
   * Rounds a number to the nearest tenth.
   * @param num - The number to round.
   * @returns The rounded number.
   */
  roundToNearestTenth(num: number): number {
    return Math.round(num * 10) / 10;
  }

  /**
   * Formats text using StringFormat.
   * @param message - The message to format.
   * @param values - The values to use in formatting.
   * @param useHTML - Whether to wrap the result in HTML paragraph tags.
   * @returns The formatted text.
   */
  formatText(
    message: string,
    values: unknown[] | Record<string, unknown>,
    useHTML = true
  ): string {
    try {
      const valueArray = Array.isArray(values) ? values : Object.values(values);
      let valueIndex = 0;

      // Replace all {{...}} occurrences with their values
      const formattedText = message.replace(/\{\{([^}]+)\}\}/g, () => {
        if (valueIndex >= valueArray.length) {
          throw new Error(`Not enough values provided for placeholders`);
        }
        const value = valueArray[valueIndex];
        valueIndex++;
        return String(value);
      });

      return useHTML ? `<p>${formattedText}</p>` : formattedText;
    } catch (error) {
      throw new Error(
        `Error formatting text: ${(error as Error).message}, original message: ${message} with values: ${JSON.stringify(values)}`
      );
    }
  }

  /**
   * Gets today's date in MM/DD format.
   * @returns Today's date as a string.
   */
  getTodaysDate(): string {
    const d = new Date();
    const month = d.getMonth() + 1;
    const date = d.getDate();
    return `${month}/${date}`;
  }

  /**
   * Checks if an object or array is empty.
   * @param obj - The object or array to check.
   * @returns True if empty, false otherwise.
   */
  isEmpty(obj: object | unknown[]): boolean {
    return (
      [Object, Array].includes(
        (obj || {}).constructor as ObjectConstructor | ArrayConstructor
      ) && !Object.entries(obj || {}).length
    );
  }

  /**
   * Gets the intersection of multiple arrays.
   * @param arrays - The arrays to intersect.
   * @returns The intersection of the arrays.
   */
  intersection<T>(arrays: T[][]): T[] {
    return arrays.reduce((a, b) => a.filter((c) => b.includes(c)));
  }

  /**
   * Returns a sample of n elements from an array.
   * @param arr - The array to sample from.
   * @param n - The number of elements to sample.
   * @returns An array of sampled elements.
   */
  getSample<T>(arr: T[], n: number): T[] {
    const result: T[] = new Array(n);
    let len = arr.length;
    const taken: number[] = new Array(len);

    if (n > len) {
      throw new RangeError('getSample: more elements taken than available');
    }

    while (n--) {
      const x = Math.floor(Math.random() * len);
      result[n] = arr[x in taken ? taken[x] : x];
      taken[x] = --len in taken ? taken[len] : len;
    }

    return result;
  }

  /**
   * Capitalizes the first letter of a string.
   * @param str - The string to capitalize.
   * @returns The capitalized string.
   */
  capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  /**
   * Gets the class list of an element with chainable methods.
   * @param elt - The element to get the class list from.
   * @returns An object with chainable methods for manipulating classes.
   */
  classList(elt: HTMLElement) {
    const list = elt.classList;

    return {
      toggle: function (c: string) {
        list.toggle(c);
        return this;
      },
      add: function (c: string) {
        list.add(c);
        return this;
      },
      remove: function (c: string) {
        list.remove(c);
        return this;
      },
    };
  }

  /**
   * Creates a unique key for HTML element :keys.
   * @returns A unique string key.
   */
  createUniqueKey(): string {
    return Math.random().toString(36).substring(2, 32);
  }

  /**
   * Flattens an object, creating dot-notation keys for nested properties.
   * @param obj - The object to flatten.
   * @param parentKey - The parent key for nested properties.
   * @returns A flattened object.
   */
  flattenObject(
    obj: Record<string, unknown>,
    parentKey = ''
  ): Record<string, unknown> {
    let flattenedObject: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(obj)) {
      const newKey = parentKey ? `${parentKey}.${key}` : key;

      if (typeof value === 'object' && value !== null) {
        flattenedObject = {
          ...flattenedObject,
          ...this.flattenObject(value as Record<string, unknown>, newKey),
        };
      } else {
        flattenedObject[newKey] = value;
      }
    }

    return flattenedObject;
  }

  /**
   * Sets the gamePlayStore reference for updating typedChars
   * @param store - The gamePlayStore instance
   */
  setGamePlayStore(store: { setTypedChars: (chars: string) => void }): void {
    this.gamePlayStore = store;
  }

  /**
   * Simulates typing characters with a delay.
   * @param typedText - The text to type out.
   */
  characters(typedText: string): void {
    this.timeouts.forEach((timeout) => clearTimeout(timeout));
    this.timeouts = [];

    const chars = typedText.split('');
    this.typedChars = '';

    // Also update the gamePlayStore if it's set
    if (this.gamePlayStore && this.gamePlayStore.setTypedChars) {
      this.gamePlayStore.setTypedChars('');
    }

    chars.forEach((char, index) => {
      const typedChar = setTimeout(() => {
        this.typedChars += char;

        // Also update the gamePlayStore if it's set
        if (this.gamePlayStore && this.gamePlayStore.setTypedChars) {
          this.gamePlayStore.setTypedChars(this.typedChars);
        }

        if (index === chars.length - 1) {
          clearTimeout(typedChar);
        }
      }, index * this.delay);

      this.timeouts.push(typedChar);
    });
  }
}
